import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AdmytService } from '../../../services/admyt.service'
import { Location } from '@angular/common'
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore'
import { FirestoreStructure } from '@parkupp/core'

interface AdmytTenant {
    id: string
    name: string
    zones: string[]
    baysNumber?: number
    status?: string
    centreId?: string
}

interface AdmytTenantUser {
    id: string
    firstName: string
    lastName: string
    email: string
    status?: string
    cars?: any[]
}

interface LinkedParkUppUser {
    uid: string
    firstName: string
    lastName: string
}

@Component({
    selector: 'app-admin-view-admyt-tenant',
    templateUrl: './admin-view-admyt-tenant.component.html',
    styleUrls: ['./admin-view-admyt-tenant.component.scss'],
})
export class AdminViewAdmytTenantComponent implements OnInit {
    centerId: string
    tenantId: string
    tenant: AdmytTenant | null = null
    tenantUsers: AdmytTenantUser[] = []
    loading = true
    error: string | null = null
    usersLoading = false
    deactivating = false

    // Map of Admyt user email to linked ParkUpp user
    linkedUsers: Map<string, LinkedParkUppUser> = new Map()

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private admytService: AdmytService,
        private firestore: Firestore
    ) {}

    async ngOnInit() {
        this.centerId = this.route.snapshot.params['centerId']
        this.tenantId = this.route.snapshot.params['tenantId']
        await this.loadTenantDetails()
        await this.loadTenantUsers()
    }

    async loadTenantDetails() {
        try {
            this.loading = true
            this.error = null

            // Get tenant details
            const tenantResult = await this.admytService.getTenant(this.centerId, this.tenantId)
            console.log('Tenant result:', tenantResult)
            this.tenant = (tenantResult.data as any) || null

            if (!this.tenant) {
                this.error = `Tenant with ID "${this.tenantId}" not found in center "${this.centerId}".`
            }

            this.loading = false
        } catch (error) {
            console.error('Failed to load tenant details:', error)
            this.error = 'Failed to load tenant details. Please try again.'
            this.loading = false
        }
    }

    async loadTenantUsers() {
        try {
            this.usersLoading = true

            // Get tenant users
            const usersResult = await this.admytService.getTenantUsers(this.centerId, this.tenantId)
            const usersResultData = usersResult.data as any
            console.log('Tenant users result:', usersResultData)
            this.tenantUsers = usersResultData.data as AdmytTenantUser[]

            // Look up linked ParkUpp users by email
            await this.loadLinkedParkUppUsers()

            this.usersLoading = false
        } catch (error) {
            console.error('Failed to load tenant users:', error)
            this.usersLoading = false
        }
    }

    async loadLinkedParkUppUsers() {
        this.linkedUsers.clear()

        // Get unique emails from tenant users
        const emails = this.tenantUsers.map(user => user.email).filter(email => !!email)
        if (emails.length === 0) return

        try {
            // Query admin clients collection to find users by email
            // We query the admin clients collection which stores the email
            const adminClientsRef = collection(this.firestore, FirestoreStructure.ADMIN_CLIENTS)

            for (const email of emails) {
                try {
                    const q = query(adminClientsRef, where('email', '==', email))
                    const snapshot = await getDocs(q)

                    if (!snapshot.empty) {
                        const doc = snapshot.docs[0]
                        const data = doc.data()

                        // Also get the client's name from the clients collection
                        const clientsRef = collection(this.firestore, FirestoreStructure.CLIENTS)
                        const clientDoc = await getDocs(query(clientsRef, where('__name__', '==', doc.id)))

                        let firstName = ''
                        let lastName = ''

                        if (!clientDoc.empty) {
                            const clientData = clientDoc.docs[0].data()
                            firstName = clientData['firstName'] || ''
                            lastName = clientData['lastName'] || ''
                        }

                        this.linkedUsers.set(email, {
                            uid: doc.id,
                            firstName,
                            lastName,
                        })
                    }
                } catch (err) {
                    console.error(`Failed to look up user by email ${email}:`, err)
                }
            }

            console.log('Linked ParkUpp users:', this.linkedUsers)
        } catch (error) {
            console.error('Failed to load linked ParkUpp users:', error)
        }
    }

    getLinkedUser(email: string): LinkedParkUppUser | null {
        return this.linkedUsers.get(email) || null
    }

    navigateToClient(uid: string) {
        this.router.navigate(['/admin/client', uid])
    }

    /**
     * Parse subscription ID from tenant name
     * Tenant names follow pattern: "{FirstName} {LastName} {subscriptionId}"
     */
    getSubscriptionId(): string | null {
        if (!this.tenant?.name) return null
        const parts = this.tenant.name.split(' ')
        if (parts.length >= 3) {
            return parts[parts.length - 1] // Last part is subscription ID
        }
        return null
    }

    /**
     * Get status class for styling
     */
    getStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'admyt-status admyt-status--active'
            case 'inactive':
                return 'admyt-status admyt-status--inactive'
            case 'pending':
                return 'admyt-status admyt-status--pending'
            default:
                return 'admyt-status admyt-status--inactive'
        }
    }

    goBack() {
        this.location.back()
    }

    goToCenterView() {
        this.router.navigate(['/admin/admyt-center', this.centerId])
    }

    async refreshData() {
        await this.loadTenantDetails()
        await this.loadTenantUsers()
    }

    async deleteUser(userId: string) {
        if (!confirm(`Are you sure you want to delete this user from the tenant?`)) {
            return
        }
        const result = await this.admytService.deleteTenantUser(this.centerId, this.tenantId, userId)
        console.log('Delete user result:', result)
        await this.loadTenantUsers()
    }

    async deactivateTenant() {
        if (!this.tenant) {
            return
        }

        const confirmed = confirm(
            `Are you sure you want to deactivate tenant "${this.tenant.name}"?\n\n` +
            `This will remove the tenant from Admyt access control. ` +
            `All users under this tenant will lose their parking access.`
        )

        if (!confirmed) {
            return
        }

        try {
            this.deactivating = true
            const result = await this.admytService.deactivateTenant(this.centerId, this.tenantId)
            console.log('Deactivate tenant result:', result)
            alert('Tenant successfully deactivated in Admyt.')
            await this.loadTenantDetails()
        } catch (error: any) {
            console.error('Failed to deactivate tenant:', error)
            alert(`Failed to deactivate tenant: ${error.message || 'Unknown error'}`)
        } finally {
            this.deactivating = false
        }
    }

    /**
     * Truncate any value (string or number) to specified length
     */
    truncate(value: any, maxLength: number): string {
        if (value === null || value === undefined) return ''
        const str = String(value)
        if (str.length <= maxLength) return str
        return str.substring(0, maxLength) + '...'
    }
}
